import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { registry } from '../../core/Registry.js';

const Wrapper = styled.div`
  width: ${props => props.width || '100%'};
  max-width: 800px;
  margin: ${props => props.align === 'center' ? '0 auto' : '0'};
  background: var(--glass-1);
  padding: 30px;
  border-radius: 16px;
  border: 1px solid var(--glass-border);
  backdrop-filter: blur(10px);
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;

  h3 { margin: 0 0 10px 0; font-size: 24px; color: var(--text-primary); }
  p { margin: 0; color: var(--text-muted); }
`;

const Sliders = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  margin-bottom: 30px;
`;

const ControlGroup = styled.div`
  background: rgba(0,0,0,0.2);
  padding: 20px;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.05);

  label {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 15px;

    span.val { color: var(--accent); font-size: 18px; font-weight: 800; }
  }

  input[type=range] {
    width: 100%;
    accent-color: var(--accent);
  }
`;

const Result = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px dashed var(--glass-border);
  padding-top: 25px;

  .price-block {
    display: flex;
    flex-direction: column;

    span.lbl { font-size: 12px; color: var(--text-muted); text-transform: uppercase; }
    span.amt { font-size: 32px; font-weight: 900; color: var(--text-primary); }
  }

  button {
    background: linear-gradient(135deg, var(--accent), var(--accent-neon));
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 15px 30px;
    font-size: 16px;
    font-weight: bold;
    cursor: pointer;
    box-shadow: 0 4px 20px var(--accent-glow);
  }

  @media(max-width: 480px) {
    flex-direction: column;
    gap: 20px;
    button { width: 100%; }
    .price-block { align-items: center; }
  }
`;

const TariffConfiguratorWidget = ({ node }) => {
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // States for filter
  const [minData, setMinData] = useState(5);
  const [sliderIndex, setSliderIndex] = useState(0);

  const dataSteps = [1, 5, 25, 50, 140, 280, 999];

  useEffect(() => {
    fetch('/api/tariffs')
      .then(res => res.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (data.tariffs || []);
        // Nur aktive Tarife
        setTariffs(list.filter(t => t.status === 'active' || !t.status));
        setLoading(false);
      })
      .catch(e => {
        console.error('Fehler beim Laden der Tarife', e);
        setLoading(false);
      });
  }, []);

  const handleSliderChange = (e) => {
    const idx = parseInt(e.target.value);
    setSliderIndex(idx);
    setMinData(dataSteps[idx]);
  };

  // Finde besten/günstigsten passenden Tarif
  const matchedTariff = useMemo(() => {
    if (tariffs.length === 0) return null;
    let matching = tariffs.filter(t => {
      const label = String(t.dataLabel || '').toUpperCase();
      if (minData === 999) return label.includes('UNLIMITED');
      if (label.includes('UNLIMITED')) return true; // Unlimited deckt alles ab
      const gb = parseInt(t.dataLabel) || 0;
      return gb >= minData;
    });

    if (matching.length === 0) return null;
    // Sortiere nach Preis aufsteigend (promoPrice bevorzugt)
    matching.sort((a, b) => {
      const pA = parseFloat(a.promoPrice || a.price) || 0;
      const pB = parseFloat(b.promoPrice || b.price) || 0;
      return pA - pB;
    });
    return matching[0];
  }, [tariffs, minData]);

  const displayData = minData === 999 ? 'Unlimited' : `Mind. ${minData} GB`;

  return (
    <Wrapper align={node.style?.desktop?.align}>
      <Header>
        <h3>{node.content?.title || 'Tarif-Baukasten'}</h3>
        <p>{node.content?.subtitle || 'Finde den passenden Tarif für deinen Datenverbrauch.'}</p>
      </Header>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>Lade Tarife...</div>
      ) : (
        <>
          <Sliders>
            <ControlGroup>
              <label>
                Dein Daten-Bedarf
                <span className="val">{displayData}</span>
              </label>
              <input 
                type="range" 
                min="0" 
                max={dataSteps.length - 1} 
                value={sliderIndex} 
                onChange={handleSliderChange} 
              />
              <div style={{display:'flex',justifyContent:'space-between', fontSize:10, color:'var(--text-muted)', marginTop:8}}>
                <span>Wenig-Surfer</span>
                <span>Maximalist</span>
              </div>
            </ControlGroup>
          </Sliders>

          <Result>
            <div className="price-block">
              {matchedTariff ? (
                <>
                  <span className="lbl">{matchedTariff.title || matchedTariff.name} ({matchedTariff.segment || matchedTariff.category || 'O2'}) {matchedTariff.group && matchedTariff.group !== 'O2' && <span style={{background:'var(--accent)', color:'#fff', padding:'2px 4px', borderRadius:4, fontSize:9, marginLeft:4}}>{matchedTariff.group}</span>}</span>
                  <span className="amt">€{parseFloat(matchedTariff.promoPrice || matchedTariff.price).toFixed(2)}</span>
                  {matchedTariff.grow ? <div style={{fontSize:10, color:'var(--accent)', marginTop:4, fontWeight:'bold', letterSpacing:'0.05em'}}>⭐ +{matchedTariff.grow} GB O₂ Grow / Jahr</div> : null}
                </>
              ) : (
                <>
                  <span className="lbl">Kein Tarif gefunden</span>
                  <span className="amt">--</span>
                </>
              )}
            </div>
            <button className="tef-bubbles" disabled={!matchedTariff} style={{ opacity: matchedTariff ? 1 : 0.5 }}>
              Auswählen
            </button>
          </Result>
        </>
      )}
    </Wrapper>
  );
};

registry.register('tariff_configurator', {
  label: 'Tarif Baukasten',
  icon: '⚙️',
  category: 'tef_specific',
  defaultContent: {},
  defaultStyle: { desktop: { align: 'center' } },
  render: TariffConfiguratorWidget,
  inspectorControls: [
    { tab: 'content', groups: [{ title: 'Texte', fields: [{name:'content.title', label:'Titel', type:'text'}, {name:'content.subtitle', label:'Untertext', type:'text'}] }] },
    { tab: 'style', groups: [{ title: 'Layout', fields: [{name:'style.desktop.align', label:'Ausrichtung', type:'select', options:[{label:'Zentriert', value:'center'},{label:'Links', value:'flex-start'}]}] }] }
  ]
});
